import { useEffect, useState } from 'react';
import { Button, TextInput } from '@carbon/react';
import type { Game, KQ, Man } from './types';
import { BACCARAT, MAT_BAU_CUA, RONG_HO, XUC_XAC } from './types';

// Bo chon ket qua rieng tung game. Controlled: value = lenh ep dang chon (null = chua du),
// onChange(k) moi khi nguoi dung doi. Phan chon do dang (Bau Cua 2/3 mat, Keno 12/20 so) giu o
// state cua bo chon, chi bao len khi du.

type P = { value: KQ | null; onChange: (k: KQ | null) => void };

function Nut({ on, children, onClick, className, title }: { on: boolean; children: React.ReactNode; onClick: () => void; className?: string; title?: string }) {
  return (
    <button type="button" title={title} className={'fr-chip' + (on ? ' is-on' : '') + (className ? ' ' + className : '')} aria-pressed={on} onClick={onClick}>
      {children}
    </button>
  );
}

// Ba vien xuc xac, moi vien chon mot mat (min..max). xs[i] = null: chua chon.
function BaVien({ xs, min, onSet, nhan }: { xs: (number | null)[]; min: 0 | 1; onSet: (i: number, v: number) => void; nhan: (v: number) => string }) {
  return (
    <div className="fr-dice">
      {[0, 1, 2].map((i) => (
        <div key={i} className="fr-dice__col" role="group" aria-label={'Viên ' + (i + 1)}>
          <span className="fr-label">Viên {i + 1}</span>
          <div className="fr-dice__faces">
            {[0, 1, 2, 3, 4, 5].map((j) => {
              const v = j + min;
              return <Nut key={v} on={xs[i] === v} onClick={() => onSet(i, v)} className={'fr-chip--face' + (min === 1 ? ' fr-chip--glyph' : '')} title={min === 1 ? String(v) : nhan(v)}>{nhan(v)}</Nut>;
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

const xucTu = (v: KQ | null, min: number, max: number): (number | null)[] => {
  const x = v?.xuc_xac;
  return Array.isArray(x) && x.length === 3 && x.every((n) => Number.isInteger(n) && n >= min && n <= max) ? (x as number[]).slice() : [null, null, null];
};

function TaiXiu({ value, onChange }: P) {
  const [xs, setXs] = useState<(number | null)[]>(() => xucTu(value, 1, 6));
  // Gia tri null (dang chon do) khong xoa cac vien da chon; gia tri moi tu ngoai thi dong bo lai
  useEffect(() => { if (value) setXs(xucTu(value, 1, 6)); }, [value]);
  const du = xs.every((x) => x !== null);
  const tong = du ? (xs as number[]).reduce((a, b) => a + b, 0) : null;
  const ben = value?.cua ?? (tong !== null ? (tong >= 11 ? 'tai' : 'xiu') : null);
  return (
    <div className="stack fr-gap">
      <div className="fr-big2">
        <Nut on={ben === 'tai'} onClick={() => onChange({ cua: 'tai' })} className="fr-chip--big fr-tai">TÀI<small>11 - 17</small></Nut>
        <Nut on={ben === 'xiu'} onClick={() => onChange({ cua: 'xiu' })} className="fr-chip--big fr-xiu">XỈU<small>4 - 10</small></Nut>
      </div>
      <p className="fr-label">Hoặc chọn đúng 3 viên xúc xắc</p>
      <BaVien xs={xs} min={1} nhan={(v) => XUC_XAC[v - 1] ?? String(v)} onSet={(i, v) => {
        const m = xs.slice(); m[i] = v; setXs(m);
        // chua du 3 vien: bo ket qua dang chon (khong de lenh cu gui di)
        onChange(m.every((x) => x !== null) ? { xuc_xac: m as number[] } : null);
      }} />
      <p className="fr-readout">{tong !== null && value?.xuc_xac ? `Tổng ${tong}: ${tong >= 11 ? 'TÀI' : 'XỈU'}` : ben ? (ben === 'tai' ? 'TÀI, xúc xắc ngẫu nhiên trong cửa Tài' : 'XỈU, xúc xắc ngẫu nhiên trong cửa Xỉu') : 'Chưa chọn'}</p>
    </div>
  );
}

function BauCua({ value, onChange }: P) {
  const [xs, setXs] = useState<(number | null)[]>(() => xucTu(value, 0, 5));
  useEffect(() => { if (value) setXs(xucTu(value, 0, 5)); }, [value]);
  return (
    <div className="stack fr-gap">
      <BaVien xs={xs} min={0} nhan={(v) => MAT_BAU_CUA[v] ?? String(v)} onSet={(i, v) => {
        const m = xs.slice(); m[i] = v; setXs(m);
        onChange(m.every((x) => x !== null) ? { xuc_xac: m as number[] } : null);
      }} />
      <p className="fr-readout">{xs.every((x) => x !== null) ? (xs as number[]).map((x) => MAT_BAU_CUA[x]).join(' · ') : 'Chọn đủ 3 mặt'}</p>
    </div>
  );
}

const XOC_DIA = [
  { so_do: 4, cua: '4 Đỏ, Chẵn' }, { so_do: 3, cua: '3 Đỏ 1 Trắng, Lẻ' }, { so_do: 2, cua: 'Chẵn' },
  { so_do: 1, cua: '3 Trắng 1 Đỏ, Lẻ' }, { so_do: 0, cua: '4 Trắng, Chẵn' },
];
function XocDia({ value, onChange }: P) {
  return (
    <div className="fr-grid5">
      {XOC_DIA.map((x) => (
        <Nut key={x.so_do} on={value?.so_do === x.so_do} onClick={() => onChange({ so_do: x.so_do })} className="fr-chip--coin">
          <span className="fr-coins" aria-hidden>{[0, 1, 2, 3].map((i) => <i key={i} className={i < x.so_do ? 'is-do' : 'is-trang'} />)}</span>
          <span>{x.so_do} đỏ {4 - x.so_do} trắng</span>
          <small>{x.cua}</small>
        </Nut>
      ))}
    </div>
  );
}

function Sicbo({ value, onChange }: P) {
  const [xs, setXs] = useState<(number | null)[]>(() => xucTu(value, 1, 6));
  // Gia tri null (dang chon do) khong xoa cac vien da chon; gia tri moi tu ngoai thi dong bo lai
  useEffect(() => { if (value) setXs(xucTu(value, 1, 6)); }, [value]);
  const ct = Array.isArray(value?.cua_thang) ? (value.cua_thang as number[]).join() : '';
  const du = xs.every((x) => x !== null) && !!value?.xuc_xac;
  const tong = du ? (xs as number[]).reduce((a, b) => a + b, 0) : null;
  const boBa = du && xs[0] === xs[1] && xs[1] === xs[2];
  return (
    <div className="stack fr-gap">
      <div className="row">
        <Nut on={ct === '3'} onClick={() => onChange({ cua_thang: [3] })}>Tài (xúc xắc ngẫu nhiên)</Nut>
        <Nut on={ct === '4'} onClick={() => onChange({ cua_thang: [4] })}>Xỉu (xúc xắc ngẫu nhiên)</Nut>
        <Nut on={ct === '11'} onClick={() => onChange({ cua_thang: [11] })}>Bộ ba bất kỳ</Nut>
      </div>
      <p className="fr-label">Hoặc chọn đúng 3 viên xúc xắc</p>
      <BaVien xs={xs} min={1} nhan={(v) => XUC_XAC[v - 1] ?? String(v)} onSet={(i, v) => {
        const m = xs.slice(); m[i] = v; setXs(m);
        // chua du 3 vien: bo ket qua dang chon (khong de lenh cu gui di)
        onChange(m.every((x) => x !== null) ? { xuc_xac: m as number[] } : null);
      }} />
      <p className="fr-readout">{tong !== null ? `Tổng ${tong}: ${boBa ? 'bộ ba (Tài, Xỉu, Chẵn, Lẻ đều thua)' : (tong >= 11 ? 'Tài' : 'Xỉu') + ', ' + (tong % 2 ? 'Lẻ' : 'Chẵn')}` : ct ? 'Cửa ' + ct : 'Chưa chọn'}</p>
    </div>
  );
}

function RongHo({ value, onChange }: P) {
  const ct = Array.isArray(value?.cua_thang) ? (value.cua_thang as number[]).join() : '';
  return (
    <div className="fr-big3">
      {[1, 2, 3].map((c) => (
        <Nut key={c} on={ct === String(c)} onClick={() => onChange({ cua_thang: [c] })} className={'fr-chip--big' + (c === 1 ? ' fr-rong' : c === 2 ? ' fr-ho' : '')}>{RONG_HO[c]?.toUpperCase()}</Nut>
      ))}
    </div>
  );
}

function Baccarat({ value, onChange }: P) {
  const ct = Array.isArray(value?.cua_thang) ? (value.cua_thang as number[]) : [];
  const chinh = ct.find((c) => c >= 1 && c <= 3) ?? null;
  const phu: number[] = ct.filter((c) => c === 4 || c === 5);
  const dat = (c: number | null, p: number[]) => onChange(c ? { cua_thang: [c, ...p].sort((a, b) => a - b) } : null);
  return (
    <div className="stack fr-gap">
      <div className="fr-big3">
        {[1, 2, 3].map((c) => (
          <Nut key={c} on={chinh === c} onClick={() => dat(c, phu)} className={'fr-chip--big' + (c === 1 ? ' fr-con' : c === 2 ? ' fr-cai' : '')}>{BACCARAT[c]?.toUpperCase()}</Nut>
        ))}
      </div>
      <div className="row">
        {[4, 5].map((c) => (
          <Nut key={c} on={phu.includes(c)} onClick={() => dat(chinh, phu.includes(c) ? phu.filter((x) => x !== c) : [...phu, c])}>{BACCARAT[c]}</Nut>
        ))}
      </div>
      <p className="fr-readout">{chinh ? 'Bài chia ngẫu nhiên đến khi đúng các cửa đã chọn thắng, không thêm cửa khác (không có ván như vậy thì các cửa đã chọn cùng thắng)' : 'Chọn Con, Cái hoặc Hòa'}</p>
    </div>
  );
}

function Xeng({ value, onChange }: P) {
  return (
    <div className="row">
      {[3, 4, 5, 6, 7, 8, 9, 10].map((b) => <Nut key={b} on={value?.bt === b} onClick={() => onChange({ bt: b })}>Biểu tượng {b}</Nut>)}
    </div>
  );
}

const DIEM_NO = [1, 1.1, 1.2, 1.5, 2, 3, 5, 10, 20, 50, 100];
function Aviator({ value, onChange }: P) {
  const [s, setS] = useState(typeof value?.diem_no === 'number' ? String(value.diem_no) : '');
  // Chi ghi de o nhap khi gia tri tu ngoai doi (bam nut, chon tu bang); dang go do (vd "2.") thi giu nguyen
  useEffect(() => {
    const v = value?.diem_no;
    if (typeof v === 'number') setS((cu) => (Math.floor(Math.round(Number(cu.replace(',', '.')) * 10000) / 100) / 100 === v ? cu : String(v)));
  }, [value]);
  const n = Number(s.replace(',', '.'));
  return (
    <div className="stack fr-gap">
      <div className="row">
        {DIEM_NO.map((d) => <Nut key={d} on={value?.diem_no === d} onClick={() => onChange({ diem_no: d })}>{d.toFixed(2)}x</Nut>)}
      </div>
      <TextInput id="fr-diem-no" labelText="Điểm nổ (1 đến 1000)" value={s} placeholder="2.35"
        onChange={(e) => {
          setS(e.target.value);
          const x = Number(e.target.value.replace(',', '.'));
          onChange(x >= 1 && x <= 1000 ? { diem_no: Math.floor(Math.round(x * 10000) / 100) / 100 } : null);
        }}
        invalid={s !== '' && !(n >= 1 && n <= 1000)} invalidText="Từ 1 đến 1000" />
    </div>
  );
}

function Keno({ value, onChange }: P) {
  const tu = (v: KQ | null) => new Set(Array.isArray(v?.so) ? (v.so as number[]) : []);
  const [chon, setChon] = useState<Set<number>>(() => tu(value));
  // chi dong bo khi co ket qua du (chon tu bang lai/lo); dang chon do dang thi giu nguyen
  useEffect(() => { if (value) setChon(tu(value)); }, [value]);
  const dat = (s: Set<number>) => { setChon(s); onChange(s.size === 20 ? { so: [...s].sort((a, b) => a - b) } : null); };
  return (
    <div className="stack fr-gap">
      <div className="fr-keno" role="group" aria-label="80 số Keno">
        {Array.from({ length: 80 }, (_, i) => i + 1).map((n) => (
          <Nut key={n} on={chon.has(n)} className="fr-chip--num" onClick={() => {
            const s = new Set(chon);
            if (s.has(n)) s.delete(n); else if (s.size < 20) s.add(n);
            dat(s);
          }}>{n}</Nut>
        ))}
      </div>
      <div className="row">
        <span className="fr-readout">{chon.size}/20 số</span>
        <Button size="sm" kind="tertiary" onClick={() => {
          const s = new Set(chon);
          while (s.size < 20) s.add(1 + Math.floor(Math.random() * 80));
          dat(s);
        }}>Điền ngẫu nhiên cho đủ 20</Button>
        <Button size="sm" kind="ghost" onClick={() => dat(new Set())}>Xóa</Button>
      </div>
    </div>
  );
}

function Lode({ value, onChange }: P) {
  const [de, setDe] = useState(typeof value?.de === 'string' ? value.de : '');
  const [db, setDb] = useState(typeof value?.special === 'string' ? value.special : '');
  useEffect(() => {
    if (!value) return;
    setDe(typeof value.de === 'string' ? value.de : '');
    setDb(typeof value.special === 'string' ? value.special : '');
  }, [value]);
  return (
    <div className="stack fr-gap">
      <TextInput id="fr-de" labelText="Đề: 2 số cuối giải đặc biệt" value={de} placeholder="68" maxLength={2}
        onChange={(e) => {
          const v = e.target.value.replace(/\D/g, '').slice(0, 2);
          setDe(v); setDb('');
          onChange(/^\d{2}$/.test(v) ? { de: v } : null);
        }} />
      <TextInput id="fr-db" labelText="Hoặc cả giải đặc biệt, 5 chữ số" value={db} placeholder="12368" maxLength={5}
        onChange={(e) => {
          const v = e.target.value.replace(/\D/g, '').slice(0, 5);
          setDb(v); setDe('');
          onChange(/^\d{5}$/.test(v) ? { special: v } : null);
        }} />
      <p className="fr-readout">Các giải khác quay ngẫu nhiên.</p>
    </div>
  );
}

export function ChonKetQua({ man, value, onChange }: P & { man: Man; game: Game }) {
  switch (man) {
    case 'tai_xiu': return <TaiXiu value={value} onChange={onChange} />;
    case 'bau_cua': return <BauCua value={value} onChange={onChange} />;
    case 'xoc_dia': return <XocDia value={value} onChange={onChange} />;
    case 'sicbo': return <Sicbo value={value} onChange={onChange} />;
    case 'rong_ho': return <RongHo value={value} onChange={onChange} />;
    case 'baccarat': return <Baccarat value={value} onChange={onChange} />;
    case 'xeng': return <Xeng value={value} onChange={onChange} />;
    case 'aviator': return <Aviator value={value} onChange={onChange} />;
    case 'keno': return <Keno value={value} onChange={onChange} />;
    case 'lode': return <Lode value={value} onChange={onChange} />;
    default: return null;
  }
}
